/**
 * In-realm bridge transport for plugin local-dev and contract testing.
 *
 * Calling `pushTheme(...)`, `pushLocale(...)`, etc. invokes the registered
 * subscriber callbacks **synchronously** — no serialisation, no port. This
 * lets Vitest + React Testing Library drive bridge state changes with `act()`
 * without a real MessageChannel.
 *
 * In production, the bridge client is `createPortBridgeClient` backed by a
 * real MessagePort. `InMemoryBridgeTransport` is the dev/test equivalent:
 * both expose the same `PortBridgeClient`-compatible subscriber API on the
 * consumer side, but `InMemoryBridgeTransport` also exposes the push-side
 * and the `onChromeRequest` handler for test assertions.
 */
import type { PortBridgeClient, ThemePayload, LocalePayload, DensityPayload, A11yPayload, NavPayload, SessionTokenPayload } from "../plugin/bridge-client";

type ChromeRequestHandler = (
  action: string,
  payload: Record<string, unknown>,
) => Promise<unknown> | unknown;

export class InMemoryBridgeTransport implements PortBridgeClient {
  private _themeCb:   ((p: ThemePayload)   => void) | undefined;
  private _localeCb:  ((p: LocalePayload)  => void) | undefined;
  private _densityCb: ((p: DensityPayload) => void) | undefined;
  private _a11yCb:    ((p: A11yPayload)    => void) | undefined;
  private _navCb:     ((p: NavPayload)     => void) | undefined;
  private _tokenCb:   ((p: SessionTokenPayload) => void) | undefined;
  private _chromeHandler: ChromeRequestHandler | undefined;

  // ── PortBridgeClient subscriber interface ──────────────────────────────────

  onTheme(cb: (p: ThemePayload) => void):   void { this._themeCb   = cb; }
  onLocale(cb: (p: LocalePayload) => void): void { this._localeCb  = cb; }
  onDensity(cb: (p: DensityPayload) => void): void { this._densityCb = cb; }
  onA11y(cb: (p: A11yPayload) => void):     void { this._a11yCb    = cb; }
  onNav(cb: (p: NavPayload) => void):        void { this._navCb     = cb; }
  onSessionToken(cb: (p: SessionTokenPayload) => void): void { this._tokenCb = cb; }

  requestChrome(action: string, payload: Record<string, unknown>): Promise<unknown> {
    return this.simulateChromeRequest(action, payload);
  }

  announceA11y(_message: string, _politeness: "polite" | "assertive"): void {
    // No-op in the mock — tests assert via `onChromeRequest` or inspect DOM.
  }

  // ── Test / dev control surface ─────────────────────────────────────────────

  /** Push a theme update to the registered subscriber (synchronous). */
  pushTheme(payload: ThemePayload):     void { this._themeCb?.(payload);   }
  /** Push a locale update to the registered subscriber. */
  pushLocale(payload: LocalePayload):   void { this._localeCb?.(payload);  }
  /** Push a density update. */
  pushDensity(payload: DensityPayload): void { this._densityCb?.(payload); }
  /** Push a11y preference changes. */
  pushA11y(payload: A11yPayload):       void { this._a11yCb?.(payload);    }
  /** Push a nav state update. */
  pushNav(payload: NavPayload):          void { this._navCb?.(payload);     }
  /** Push a frontend-session token. */
  pushSessionToken(payload: SessionTokenPayload): void { this._tokenCb?.(payload); }

  /**
   * Register a handler for plugin→host chrome requests (toast, confirm, etc).
   * Called by `requestChrome` and by `simulateChromeRequest`.
   */
  onChromeRequest(handler: ChromeRequestHandler): void {
    this._chromeHandler = handler;
  }

  /**
   * Programmatically send a chrome request as if a plugin component called
   * `PortBridgeClient.requestChrome(...)`. Useful for test assertions.
   */
  async simulateChromeRequest(action: string, payload: Record<string, unknown>): Promise<unknown> {
    if (this._chromeHandler === undefined) {
      return null;
    }
    return this._chromeHandler(action, payload);
  }
}
